import { useState } from 'react';

// Material-UI
import { styled, useTheme } from '@mui/material/styles';
import { TextField, Box, Grid, Typography, CircularProgress } from '@mui/material';
import { Alert } from '@mui/material';

// Components
import { Section, Header, Content } from 'components/ui/forms';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import { DatePicker, TimePicker } from 'components/ui/inputs';

// Utility
import moment from 'moment-timezone';
import { getMomentFromDateAndTime, isRichTextEmpty } from 'utility/validation';
import { CA_TIMEZONES } from 'utility/dates';
import { RichEditor } from 'components/RichEditor';
import SeasonFields from 'components/forms/SeasonFields';

/**
 * Form specifically used for creating a copy of an existing event
 * EventForm.js could have been reused, but I created a new form component in the interest of time :)
 */

const EMPTY_OPTIONS = [];

export default function EventCopyForm(props) {
    // Props
    const { defaultValues, seasons = EMPTY_OPTIONS, onSubmit, onCancel, loading } = props;

    // State
    const [values, setValues] = useState({
        title: { value: defaultValues?.title ?? '', error: false, message: '' },
        description: { value: defaultValues?.description ?? '', error: false, message: '' },
        htmlDescription: {
            value: defaultValues?.htmlDescription || `<p>${defaultValues?.description || ''}</p>`,
            error: false,
            message: '',
        },
        startDate: {
            value: defaultValues?.startDate ?? moment({ hour: 12, minute: 0 }),
            error: false,
            message: '',
        },
        startTime: {
            value: defaultValues?.startDate ?? moment({ hour: 12, minute: 0 }),
            error: false,
            message: '',
        },
        endDate: {
            value: defaultValues?.endDate ?? moment({ hour: 23, minute: 45 }),
            error: false,
            message: '',
        },
        endTime: {
            value: defaultValues?.endDate ?? moment({ hour: 23, minute: 45 }),
            error: false,
            message: '',
        },
        timezone: { value: defaultValues?.timezone ?? '', error: false, message: '' },
        rangeError: false,
        parentEventId: { value: '', error: false, message: '' },
        seasonAdmissionCapacity: { value: '', error: false, message: '' },
    });

    const { title, htmlDescription, startDate, startTime, endDate, endTime, timezone, rangeError } =
        values;

    const parentMode = Number(defaultValues?.parentMode) === 1;
    const entityLabel = parentMode ? 'Season' : 'Event';
    const selectedSeason = seasons.find(
        (season) => String(season.event_id) === String(values.parentEventId.value),
    );
    const inheritedSeating = Number(selectedSeason?.seats_flag) === 1;

    // Hooks
    const theme = useTheme();

    function handleFormChange(e) {
        const { id, value } = e.target;
        setValues({ ...values, [id]: { value, error: false, message: '' } });
    }

    function handleDescriptionChange({ html, text }) {
        setValues({
            ...values,
            description: { value: text, error: false, message: '' },
            htmlDescription: { value: html, error: false, message: '' },
        });
    }

    function handleStartDateChange(value) {
        setValues({ ...values, startDate: { value, error: false, message: '' } });
    }

    function handleStartTimeChange(value) {
        setValues({ ...values, startTime: { value, error: false, message: '' } });
    }

    function handleEndDateChange(value) {
        setValues({ ...values, endDate: { value, error: false, message: '' } });
    }

    function handleEndTimeChange(value) {
        setValues({ ...values, endTime: { value, error: false, message: '' } });
    }

    function handleSubmit() {
        let newState = { ...values };

        // Check for empty fields
        Object.keys(values).forEach((key) => {
            if (['rangeError', 'parentEventId', 'seasonAdmissionCapacity'].includes(key)) return;
            if (key === 'htmlDescription') {
                // Special validation for rich text editor
                if (isRichTextEmpty(values[key].value)) {
                    newState[key] = {
                        ...values[key],
                        error: true,
                        message: 'This field cannot be empty',
                    };
                } else {
                    newState[key] = { ...values[key] };
                }
            } else if (key === 'description') {
                // Skip validation for plain text description since it's derived from htmlDescription
                // If its empty due to a bug, we still allow it since the user can't see or conrol it
                newState[key] = { ...values[key] };
            } else {
                String(values[key].value).trim() === ''
                    ? (newState[key] = {
                          ...values[key],
                          error: true,
                          message: 'This field cannot be empty',
                      })
                    : (newState[key] = { ...values[key] });
            }
        });

        const capacity = Number(values.seasonAdmissionCapacity.value);
        newState.seasonAdmissionCapacity = {
            ...values.seasonAdmissionCapacity,
            error: false,
            message: '',
        };
        if (
            values.parentEventId.value &&
            !inheritedSeating &&
            String(values.seasonAdmissionCapacity.value).trim() !== '' &&
            (!Number.isSafeInteger(capacity) || capacity <= 0)
        ) {
            newState.seasonAdmissionCapacity = {
                ...values.seasonAdmissionCapacity,
                error: true,
                message: 'Enter a positive whole number or leave blank for no shared limit',
            };
        }

        // Check for valid start and end dates
        ['startDate', 'endDate'].forEach((key) => {
            if (!moment(values[key].value).isValid()) {
                newState[key] = {
                    ...values[key],
                    error: true,
                    message: 'Please enter a valid date',
                };
            } else {
                newState[key] = { ...values[key] };
            }
        });

        // Check for valid start and end times
        ['startTime', 'endTime'].forEach((key) => {
            if (!moment(values[key].value).isValid()) {
                newState[key] = {
                    ...values[key],
                    error: true,
                    message: 'Please enter a valid time',
                };
            } else {
                newState[key] = { ...values[key] };
            }
        });

        // Check if end date comes after start date
        if (
            !newState.startDate.error &&
            !newState.endDate.error &&
            !newState.startTime.error &&
            !newState.endTime.error
        ) {
            const fullStartDate = getMomentFromDateAndTime(
                newState.startDate.value,
                newState.startTime.value,
            );
            const fullEndDate = getMomentFromDateAndTime(
                newState.endDate.value,
                newState.endTime.value,
            );

            if (fullEndDate < fullStartDate) {
                newState.rangeError = true;
            } else {
                newState.rangeError = false;
            }
        } else {
            newState.rangeError = false;
        }

        // Return if any fields are invalid
        if (Object.keys(newState).some((key) => newState[key].error || newState.rangeError)) {
            setValues(newState);
            return null;
        }
        const startDate = getMomentFromDateAndTime(
            values.startDate.value,
            values.startTime.value,
        ).format('YYYY-MM-DD HH:mm:00');
        const endDate = getMomentFromDateAndTime(values.endDate.value, values.endTime.value).format(
            'YYYY-MM-DD HH:mm:59',
        );

        const formData = {
            title: values.title.value,
            description: values.description.value,
            htmlDescription: values.htmlDescription.value,
            startDate: startDate,
            endDate: endDate,
            timezone: values.timezone.value,
            parentMode: parentMode ? 1 : 0,
            parentEventId: values.parentEventId.value ? Number(values.parentEventId.value) : null,
            seasonAdmissionCapacity:
                values.parentEventId.value &&
                !inheritedSeating &&
                String(values.seasonAdmissionCapacity.value).trim() !== ''
                    ? capacity
                    : null,
        };
        onSubmit(formData);
    }

    return (
        <Box display="flex" justifyContent="center" width="100%">
            <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                mt={4}
                mb={5}
                maxWidth={theme.breakpoints.values.sm}
            >
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Alert severity="info">
                            This will copy your {entityLabel.toLowerCase()}, including any tickets
                            and add-ons (bundles excluded). You may change some of the details
                            below. After copying, please review all of your event details before
                            publishing.
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <Section>
                            <Header>Description</Header>

                            <Content>
                                <SdTextField
                                    id="title"
                                    value={title.value}
                                    error={title.error}
                                    helperText={title.message}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    label={`${entityLabel} Title`}
                                />

                                <RichEditor
                                    label={`${entityLabel} Description`}
                                    value={htmlDescription.value}
                                    error={htmlDescription.error}
                                    helperText={htmlDescription.message}
                                    onChange={({ html, text }) => {
                                        handleDescriptionChange({ html, text });
                                    }}
                                    placeholder="Enter event description..."
                                />
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Time & Date</Header>

                            <SubHeader>Let people know when the event is happening.</SubHeader>

                            <Content>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Box mr={1} mb={3}>
                                            <DatePicker
                                                id="startDate"
                                                value={startDate.value}
                                                error={startDate.error}
                                                helperText={startDate.message}
                                                onChange={handleStartDateChange}
                                                label="Start Date"
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Box ml={1} mb={3}>
                                            <TimePicker
                                                id="startTime"
                                                value={startTime.value}
                                                error={startTime.error}
                                                helperText={startTime.message}
                                                onChange={handleStartTimeChange}
                                                label="Start Time"
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Box mr={1} mb={3}>
                                            <DatePicker
                                                id="endDate"
                                                value={endDate.value}
                                                error={endDate.error}
                                                helperText={endDate.message}
                                                onChange={handleEndDateChange}
                                                label="End Date"
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Box ml={1} mb={3}>
                                            <TimePicker
                                                id="endTime"
                                                value={endTime.value}
                                                error={endTime.error}
                                                helperText={endTime.message}
                                                onChange={handleEndTimeChange}
                                                label="End Time"
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>

                                {rangeError && (
                                    <ErrorMessage>
                                        End date cannot come before start date
                                    </ErrorMessage>
                                )}

                                <SdTextField
                                    id="timezone"
                                    value={timezone.value}
                                    error={timezone.error}
                                    helperText={timezone.message}
                                    onChange={handleFormChange}
                                    select
                                    InputLabelProps={{ shrink: true }}
                                    SelectProps={{ native: true }}
                                    variant="outlined"
                                    label="Timezone"
                                >
                                    {timezone.value === '' && <option value=""></option>}
                                    {CA_TIMEZONES.map((tz) => (
                                        <option key={tz.name} value={tz.name}>
                                            {tz.fullName}
                                        </option>
                                    ))}
                                </SdTextField>
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <SeasonFields
                            parentMode={parentMode}
                            parentEventId={values.parentEventId.value}
                            capacity={values.seasonAdmissionCapacity.value}
                            capacityError={values.seasonAdmissionCapacity.message}
                            seasons={seasons}
                            disabled={loading}
                            seatingEnabled={defaultValues?.seatsEnabled}
                            seatingChartKey={defaultValues?.seatingChartKey}
                            inheritedSeating={inheritedSeating}
                            onChange={handleFormChange}
                        />
                    </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end">
                    <CancelButton onClick={onCancel} disabled={loading}>
                        Cancel
                    </CancelButton>
                    <SubmitButton onClick={handleSubmit} disabled={loading}>
                        {loading ? <CircularProgress color="secondary" size={18} /> : 'Copy'}
                    </SubmitButton>
                </Box>
            </Box>
        </Box>
    );
}

const SubHeader = styled(Typography)({
    marginBottom: 8,
});

const SdTextField = styled(TextField)({
    marginBottom: '24px',
    width: '100%',
});

const ErrorMessage = styled('p')(({ theme }) => ({
    color: theme.palette.error.main,
}));
